import { useCallback, useRef } from 'react'
import type { ChangeEvent, RefObject } from 'react'

import { UploadSource } from '@buffer-mono/uploader'
import type { Uploader } from '@buffer-mono/uploader'

type UseFilePickerParams = {
  uploader: Uploader
  multiple?: boolean
  accept?: string
  disabled?: boolean
}

type FilePickerInputProps = {
  ref: RefObject<HTMLInputElement>
  type: 'file'
  multiple: boolean
  accept?: string
  disabled: boolean
  style: { display: 'none' }
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

type UseFilePickerResult = {
  open: () => void
  inputProps: FilePickerInputProps
}

/**
 * Wires a hidden file input to an uploader for click-to-browse.
 * Spread `inputProps` onto a hidden `<input>` and call `open()` to prompt
 * the file picker; selected files are added to the uploader as a file-picker
 * source.
 */
export const useFilePicker = ({
  uploader,
  multiple = true,
  accept,
  disabled = false,
}: UseFilePickerParams): UseFilePickerResult => {
  const inputRef = useRef<HTMLInputElement>(null)

  const open = useCallback((): void => {
    inputRef.current?.click()
  }, [])

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const files = Array.from(event.target.files ?? [])

      if (files.length > 0) {
        uploader.addFiles(files, { source: UploadSource.filePicker() })
      }

      // Reset so selecting the same file again re-triggers change
      event.target.value = ''
    },
    [uploader],
  )

  return {
    open,
    inputProps: {
      ref: inputRef,
      type: 'file',
      multiple,
      accept,
      disabled,
      style: { display: 'none' },
      onChange,
    },
  }
}
